import { useState } from "react";
import { useUser } from "../../context/userContext";
import { updateProfile } from "../../api/userApi";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';

// LHDN tax-type codes printed on the CP58 form. The prefix is what
// goes into the small 2-cell box; the rest of the value is the digits.
const TAX_TYPE_OPTIONS = ['', 'C', 'C1', 'TN', 'PT', 'SG', 'OG', 'D', 'TP', 'TJ', 'TF', 'IG'];

// Split a stored tax string ("IG26590393090") into { type: "IG", no: "26590393090" }.
// If the string starts with digits, type is empty.
function splitTaxNo(combined) {
    if (!combined) return { type: '', no: '' };
    const m = String(combined).match(/^([A-Z]{1,2})\s*(\d.*)?$/);
    return m ? { type: m[1], no: (m[2] || '').replace(/\s+/g, '') } : { type: '', no: combined };
}

// Combine for storage. Keeps both halves trimmed.
function combineTaxNo(type, no) {
    const t = (type || '').trim().toUpperCase();
    const n = (no || '').trim();
    if (!t && !n) return '';
    return t + n;
}

// Section header used between Personal / Company / Bank groups so the
// long form is easier to scan.
const SectionHeader = ({ label }) => (
    <Typography
        sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'Poppins', sans-serif",
            mt: 1,
        }}
    >
        {label}
    </Typography>
);

export default function UpdateUserDetailsForm({ userDetails, setUserDetails }) {
    const { user } = useUser();
    const [saving, setSaving] = useState(false);

    const handleUpdateUserProfile = async () => {
        setSaving(true);
        try {
            await updateProfile({
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                alias: userDetails.alias,
                email: userDetails.email,
                phoneNumber: userDetails.phoneNumber,
                icNumber: userDetails.icNumber,
                incomeTaxNo: userDetails.incomeTaxNo,
                bankName: userDetails.bankName,
                bankAccountNumber: userDetails.bankAccountNumber,
                billingName: userDetails.billingName,
                companyRegNo: userDetails.companyRegNo,
                companyIncomeTaxNo: userDetails.companyIncomeTaxNo,
                companyAddress: userDetails.companyAddress,
                address: userDetails.address,
            });
            alert('Profile updated successfully');
        } catch (err) {
            alert('Failed to update: ' + (err.response?.data || err.message));
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (e) => setUserDetails({ ...userDetails, [k]: e.target.value });

    // Local split state for the two-input tax fields, mirrored into
    // userDetails on every change so the saved value stays a single
    // string ("IG26590393090") matching the DB column.
    const personalTax = splitTaxNo(userDetails?.incomeTaxNo);
    const companyTax = splitTaxNo(userDetails?.companyIncomeTaxNo);
    const setTax = (combinedKey, half) => (e) => {
        const next = half === 'type'
            ? combineTaxNo(e.target.value, splitTaxNo(userDetails?.[combinedKey]).no)
            : combineTaxNo(splitTaxNo(userDetails?.[combinedKey]).type, e.target.value);
        setUserDetails({ ...userDetails, [combinedKey]: next });
    };

    return (
        <div className="update-password-form-container">
            <form className="update-password-form" onSubmit={(e) => e.preventDefault()}>
                <Stack spacing={2}>
                    <Typography variant="h6">Update User Details</Typography>

                    {/* ── Personal Info ─────────────────────────────────── */}
                    <SectionHeader label="Personal Info" />

                    <TextField
                        fullWidth
                        label="Alias"
                        size="small"
                        placeholder="Alias"
                        value={userDetails?.alias || ''}
                        onChange={set('alias')}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="First Name (Given Name)" size="small"
                                value={userDetails?.firstName || ''} onChange={set('firstName')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Last Name (Surname)" size="small"
                                value={userDetails?.lastName || ''} onChange={set('lastName')} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="IC Number" size="small"
                                value={userDetails?.icNumber || ''} onChange={set('icNumber')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    label="Tax Type"
                                    size="small"
                                    sx={{ minWidth: 90 }}
                                    value={personalTax.type}
                                    onChange={setTax('incomeTaxNo', 'type')}
                                >
                                    {TAX_TYPE_OPTIONS.map(opt => (
                                        <MenuItem key={opt || 'none'} value={opt}>{opt || '—'}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth
                                    label="Income Tax No"
                                    size="small"
                                    placeholder="LHDN tax file digits"
                                    value={personalTax.no}
                                    onChange={setTax('incomeTaxNo', 'no')}
                                />
                            </Stack>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" type="email" size="small"
                                value={userDetails?.email || ''} onChange={set('email')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Phone Number" type="tel" size="small"
                                value={userDetails?.phoneNumber || ''}
                                onChange={(e) => setUserDetails({ ...userDetails, phoneNumber: formatPhoneNumber(e.target.value) })} />
                        </Grid>
                    </Grid>

                    <TextField
                        fullWidth
                        label="Address"
                        size="small"
                        placeholder="Full mailing address (used on CP58)"
                        value={userDetails?.address || ''}
                        onChange={set('address')}
                        multiline
                        minRows={2}
                    />

                    {/* ── Company Info ──────────────────────────────────── */}
                    <SectionHeader label="Company Info (if applicable)" />
                    <Box sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: -1 }}>
                        Fill these only if you operate as a registered company.
                        When set, CP58 + slip exports use the company info as the
                        recipient instead of your personal name + IC.
                    </Box>

                    <TextField
                        fullWidth
                        label="Company Name (Billing Name)"
                        size="small"
                        placeholder="e.g. ABC ENTERPRISE / SDN BHD"
                        value={userDetails?.billingName || ''}
                        onChange={set('billingName')}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Company Reg No (SSM)"
                                size="small"
                                placeholder="e.g. 199301017370 / 272108-A"
                                value={userDetails?.companyRegNo || ''}
                                onChange={set('companyRegNo')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    label="Tax Type"
                                    size="small"
                                    sx={{ minWidth: 90 }}
                                    value={companyTax.type}
                                    onChange={setTax('companyIncomeTaxNo', 'type')}
                                >
                                    {TAX_TYPE_OPTIONS.map(opt => (
                                        <MenuItem key={opt || 'none'} value={opt}>{opt || '—'}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth
                                    label="Company Income Tax No"
                                    size="small"
                                    placeholder="e.g. 5875970000"
                                    value={companyTax.no}
                                    onChange={setTax('companyIncomeTaxNo', 'no')}
                                />
                            </Stack>
                        </Grid>
                    </Grid>

                    <TextField
                        fullWidth
                        label="Company Address"
                        size="small"
                        placeholder="Registered company address (used on CP58 in company mode)"
                        value={userDetails?.companyAddress || ''}
                        onChange={set('companyAddress')}
                        multiline
                        minRows={2}
                    />

                    {/* ── Bank Info ─────────────────────────────────────── */}
                    <SectionHeader label="Bank Info" />
                    <Box sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: -1 }}>
                        Used on the commission slip Maker CSV (bank-payment file).
                        Should match the company / personal name above so the
                        bank accepts the transfer.
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Bank Name" size="small"
                                value={userDetails?.bankName || ''} onChange={set('bankName')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Bank Account Number" size="small"
                                value={userDetails?.bankAccountNumber || ''} onChange={set('bankAccountNumber')} />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            type="submit"
                            variant="contained"
                            onClick={handleUpdateUserProfile}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </div>
    );
}
